/* eslint-disable max-classes-per-file */
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, goOffline, goOnline } from 'firebase/database';
import { getAuth, signInWithCustomToken } from 'firebase/auth';

import http from '@/services/http';
import errorHandlers from '@/services/error-handlers';
import logger from '@/services/logger';

class RealTimeDataBase {
  constructor(store) {
    this.merchant = store.state.merchant;
    this.user = store.state.session.currentUser;

    this.db = null;
    this.userCredentials = null;
    this.unsubscribeFromTestConnection = null;
    this.isInitialized = false;
    this.configurationAvailable = false;
  }

  async init() {
    if (!this.isConfigurationAvailable()) {
      console.warn('Real time database configuration is missing for this merchant');
      return;
    }

    try {
      const app = RealTimeDataBase.initializeApp(this.merchant.realTimeDbConfig);

      const user = await RealTimeDataBase.authenticateUser();

      this.userCredentials = user;

      this.db = getDatabase(app);
      this.isInitialized = true;
      this.configurationAvailable = true;
    }
    catch (error) {
      console.warn('Real time database configuration is missing for this merchant');
      this.isInitialized = false;
      this.configurationAvailable = false;
    }
  }

  isConfigurationAvailable() {
    return !!(
      this.merchant.realTimeDbConfig
      && this.merchant.realTimeDbConfig.appConfigurations
      && this.merchant.realTimeDbConfig.databaseUrl
      && this.merchant.realTimeDbConfig.projectId
    );
  }

  static initializeApp(realTimeDbConfig) {
    const {
      appConfigurations,
      databaseUrl,
      gcmSenderId,
      projectId
    } = realTimeDbConfig;

    const webAppKeys = appConfigurations.find(config => config.platformType === 'Web');

    /** https://firebase.google.com/docs/database/web/start#web-modular-api */
    const firebaseProjectConfig = {
      apiKey: webAppKeys.apiKey,
      databaseURL: databaseUrl,
      projectId,
      messagingSenderId: gcmSenderId,
      appId: webAppKeys.googleAppId
    };

    const app = initializeApp(firebaseProjectConfig);

    return app;
  }

  static async authenticateUser() {
    try {
      const body = {
        grant_type: 'google_security',
        scope: 'realtime_database'
      };

      const { data } = await http.post('/cardfree-api/v1/oauth/token', body);

      const auth = getAuth();

      /** https://firebase.google.com/docs/auth/admin/create-custom-tokens#sign_in_using_custom_tokens_on_clients */
      await signInWithCustomToken(auth, data.access_token);
    }
    catch (error) {
      errorHandlers.silent(error);
    }
  }

  goOnline() {
    if (!this.isInitialized || !this.db) {
      return;
    }

    /** https://modularfirebase.web.app/reference/database.goonline */
    goOnline(this.db);

    this.testConnection();
  }

  goOffline() {
    if (!this.isInitialized || !this.db) {
      return;
    }

    /** https://modularfirebase.web.app/reference/database.gooffline */
    goOffline(this.db);

    this.testConnection();
  }

  testConnection() {
    if (!this.isInitialized || !this.db) {
      return;
    }

    const connectedRef = ref(this.db, '.info/connected');

    const unsubscribeFromTestConnection = onValue(connectedRef, (snap) => {
      if (snap.val() === true) {
        this.logInfo({ isConnected: true, db: this.db });
      }
      else {
        this.logInfo({ isConnected: false, db: this.db });
      }
    });

    this.unsubscribeFromTestConnection = unsubscribeFromTestConnection;
  }

  // eslint-disable-next-line class-methods-use-this
  logInfo(dbInfo) {
    const logStyles = [
      'color: #5186EC; font-weight: bold',
      'color: #D85040; font-weight: bold',
      'color: #F1BD42; font-weight: bold',
      'color: #5186EC; font-weight: bold',
      'color: #58A55C; font-weight: bold',
      'color: #D85040; font-weight: bold',
      'color: inherit',
      'color: #fff; padding: 1px 4px; background: #3B873E; border-radius: 4px',
      'color: inherit'
    ];

    /** Will only log info for non-prod and non-uat enviros ie. window._debugMode === 'local' || 'dev' */
    logger.info(
      '%cG%co%co%cg%cl%ce%c %cFirebase log%c:',
      ...logStyles,
      dbInfo
    );
  }
}

class OrdersRtdb extends RealTimeDataBase {
  constructor(store) {
    super(store);

    this.unsubscribeFromOrders = null;

    // Attempt initialization during construction
    this.init();
  }

  async connect(orderUpdateCb) {
    // Wait for initialization to complete if it's still in progress
    if (!this.isInitialized && this.isConfigurationAvailable()) {
      await this.init();
    }

    // If still not initialized after init attempt, exit silently
    if (!this.isInitialized || !this.db) {
      return;
    }

    this.goOnline();

    /* https://firebase.google.com/docs/database/web/read-and-write#web_value_events */
    const ordersReference = ref(this.db, `Orders/${this.merchant.internalId}/ExternalOrders/${this.user.accountId}`);

    const unsubscribeFromOrders = onValue(ordersReference, (dbSnapshot) => {
      const data = dbSnapshot.val();

      this.logInfo({ realTimeOrderData: data });

      orderUpdateCb(data);
    });

    this.unsubscribeFromOrders = unsubscribeFromOrders;
  }

  disconnect() {
    if (this.unsubscribeFromOrders) {
      this.unsubscribeFromOrders();
    }

    if (this.unsubscribeFromTestConnection) {
      this.unsubscribeFromTestConnection();
    }

    this.goOffline();
  }
}

export default OrdersRtdb;
